import React, { useReducer, useMemo, useEffect } from 'react';
import { getStorage } from '#utils/storage';
import { FiltersContext } from './FiltersContext';
import { useAccount } from '#contexts/account';

const STORAGE_KEY = 'pullRequestReviewsFilters';

const defaultFilters = {
  dateRange: { startDate: null, endDate: null },
  granularity: 'DAY',
  groupBy: 'AUTHOR',
  repositoryIds: [],
};

const convertDateStringsToObjects = (filters) => {
  const entries = Object.entries(filters).map(([id, filter]) => {
    const { startDate, endDate } = filter.dateRange;

    return [id, {
      ...filter,
      dateRange: {
        startDate: startDate ? new Date(startDate) : null,
        endDate: endDate ? new Date(endDate) : null,
      },
    }];
  });

  return Object.fromEntries(entries);
};

const parseSavedFilters = (savedFilters) => {
  try {
    const parsedFilters = JSON.parse(savedFilters);
    return convertDateStringsToObjects(parsedFilters);
  } catch {
    return {};
  }
};

const loadInitialFilters = () => {
  const storage = getStorage();
  const savedFilters = storage.get(STORAGE_KEY);
  return savedFilters ? parseSavedFilters(savedFilters) : {};
};

const filtersReducer = (state, action) => {
  const { installationId, type, payload } = action;

  if (!installationId) {
    return state;
  }

  const installationFilters = state[installationId] || defaultFilters;

  switch (type) {
    case 'SET_DATE_RANGE':
      return { ...state, [installationId]: { ...installationFilters, dateRange: payload } };
    case 'SET_GRANULARITY':
      return { ...state, [installationId]: { ...installationFilters, granularity: payload } };
    case 'SET_REPOSITORY_IDS':
      return { ...state, [installationId]: { ...installationFilters, repositoryIds: payload } };
    case 'SET_GROUP_BY':
      return { ...state, [installationId]: { ...installationFilters, groupBy: payload } };
    default:
      return state;
  }
};

const saveFiltersToStorage = (filters) => {
  const storage = getStorage();
  storage.set(STORAGE_KEY, JSON.stringify(filters));
};

export const FiltersProvider = ({ children }) => {
  const { currentInstallation } = useAccount();
  const installationId = currentInstallation?.id || '';

  const [filters, dispatch] = useReducer(filtersReducer, loadInitialFilters());

  useEffect(() => {
    if (Object.keys(filters).length) {
      saveFiltersToStorage(filters);
    }
  }, [filters]);

  const contextValue = useMemo(() => ({
    filters: filters[installationId] || defaultFilters,
    setDateRange: (dateRange) => dispatch({
      type: 'SET_DATE_RANGE',
      payload: dateRange,
      installationId,
    }),
    setGranularity: (granularity) => dispatch({
      type: 'SET_GRANULARITY',
      payload: granularity,
      installationId,
    }),
    setRepositoryIds: (ids) => dispatch({
      type: 'SET_REPOSITORY_IDS',
      payload: ids,
      installationId,
    }),
    setGroupBy: (groupBy) => dispatch({
      type: 'SET_GROUP_BY',
      payload: groupBy,
      installationId,
    }),
  }), [filters, installationId]);

  return (
    <FiltersContext.Provider value={contextValue}>
      {children}
    </FiltersContext.Provider>
  );
};
